/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Combobox,
    CONTROL_SIZE,
    Form,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
} from '@veeam-vspc/components';
import { IdentityProviderRoleMappingRule } from '@veeam-vspc/models/rest';

import type {
    WizardStep,
    WizardStepData,
} from '@veeam-vspc/components';

import { StepLayout } from 'components/layouts/StepLayout';
import { UserRoles } from 'core/enums';
import { useLang } from 'views/providers/LangProvider/hooks';
import { NoteBar } from 'components/layouts/NoteBar';
import { getResellerDocUrl } from '../../../Base/helpers/getResellerDocUrl';

import type { SSORuleModel } from '../../../Base/interfaces/sso-rule-model';


export const Role: React.FC<WizardStepData<SSORuleModel>> = ({ data, validationState, onDataChange }) => {
    const lang = useLang();

    return (
        <Form
            value={data}
            validationState={validationState}
            onChange={onDataChange}
        >
            <StepLayout
                title={lang.ROLE}
                description={lang.SPECIFY_A_ROLE_TO_ASSIGN}
            >
                <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.m}>
                    <Combobox
                        name='model.role'
                        data={[
                            { value: UserRoles.ResellerAdministrator, text: lang.SERVICE_PROVIDER_ADMINISTRATOR },
                            { value: UserRoles.ResellerOperator, text: lang.SERVICE_PROVIDER_OPERATOR },
                            { value: UserRoles.ResellerUser, text: lang.SERVICE_PROVIDER_USER },
                            { value: UserRoles.ResellerInvoiceAuditor, text: lang.SERVICE_PROVIDER_INVOICE_AUDITOR },
                        ]}
                        valueGetter={item => item.value}
                        textGetter={item => item.text}
                        size={CONTROL_SIZE.m}
                        label={lang.ROLE}
                        inlineLabel
                    />

                    <NoteBar>
                        {data.model.role === IdentityProviderRoleMappingRule.RoleEnum.ResellerAdministrator &&
                            <p>{lang.SERVICE_PROVIDER_ADMINISTRATOR_HAS_ACCESS}</p>}

                        {data.model.role === IdentityProviderRoleMappingRule.RoleEnum.ResellerOperator &&
                            <p>{lang.SERVICE_PROVIDER_OPERATOR_CAN_PERFORM}</p>}

                        {data.model.role === IdentityProviderRoleMappingRule.RoleEnum.ResellerUser &&
                            <p>{lang.SERVICE_PROVIDER_USER_HAS_ACCESS}</p>}

                        {data.model.role === IdentityProviderRoleMappingRule.RoleEnum.ResellerInvoiceAuditor &&
                            <p>{lang.SERVICE_PROVIDER_INVOICE_AUDITOR_HAS_ACCESS}</p>}

                        <p>
                            {lang.CLICK}
                            {' '}

                            <a
                                href={getResellerDocUrl()}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {lang.HERE}
                            </a>

                            {' '}
                            {lang.TO_GET_DETAILED_INFORMATION}
                        </p>
                    </NoteBar>
                </StackView>
            </StepLayout>
        </Form>
    );
};

export const getRoleStep = (title: string): WizardStep<SSORuleModel> => ({
    title,
    render: data => <Role {...data} />,
});
